/**
 * useEntitlement(key): the ONE client-side answer to "is this feature on for this brand".
 *
 * Source: `entitlements` of GET /api/teams/package (resolved features + LIFETIME_GATING_MODE).
 *
 * Semantics:
 *  - NEW keys: mode 'off' / 'shadow' or no entitlements payload -> allowed = true (nothing is ever locked in the
 *    UI before enforce). mode 'enforce' -> allowed = features[key].
 *  - LEGACY keys (a request gate enforced them before gating: wordpress, gdrive, canva, brand_check, whitelabel,
 *    plus the `analytics` base column): the resolver mirrors the old row / column logic in every mode, so
 *    allowed = features[key]; without a payload the old UI logic (usePackage().hasFeature) applies.
 *
 * Dev preview (import.meta.env.DEV only): localStorage `aa-gating-preview` forces enforce semantics on the client
 * so locked states can be screenshotted without changing the backend mode.
 *   aa-gating-preview = enforce              -> enforce with the REAL features of the team
 *   aa-gating-preview = lock:best_times,...  -> enforce AND the listed keys forced to not allowed
 *   aa-gating-preview = lock:all             -> enforce AND every new key forced to not allowed
 */

import { useContext } from 'react';
import { PackageContext } from '@/contexts/PackageContext';
import { useGatingStore } from '@/stores/gating.store';
import type { EntitlementFeatureKey, EntitlementsPayload, GatingMode } from '@/types/package';

export interface EntitlementState {
  /** The feature may be used (UI shows the real thing) */
  allowed: boolean;
  /** The UI should show the locked state (never true in mode off/shadow for new keys) */
  locked: boolean;
  /** Cheapest plan that includes the key, when known (null = unknown, the card falls back to /shop) */
  upsellPlanSlug: string | null;
  mode: GatingMode;
}

/** Keys that were enforced before lifetime gating existed (+ the analytics base column): old UI logic applies. */
const LEGACY_UI_KEYS: ReadonlySet<EntitlementFeatureKey> = new Set<EntitlementFeatureKey>([
  'wordpress',
  'gdrive',
  'canva',
  'brand_check',
  'whitelabel',
  'analytics',
]);

/** Legacy key -> usePackage().hasFeature name (fallback when the entitlements payload is missing) */
const LEGACY_HAS_FEATURE_NAME: Partial<Record<EntitlementFeatureKey, string>> = {
  wordpress: 'wordpress',
  gdrive: 'gdrive_enabled',
  canva: 'canva_enabled',
  brand_check: 'brand_check_enabled',
  whitelabel: 'whitelabel_enabled',
  analytics: 'analytics_enabled',
};

const PREVIEW_STORAGE_KEY = 'aa-gating-preview';

interface GatingPreview {
  forcedKeys: ReadonlySet<string>;
  forceAll: boolean;
}

/** Dev-only. Always null in production builds. */
const readGatingPreview = (): GatingPreview | null => {
  if (!import.meta.env.DEV) return null;
  try {
    const raw = window.localStorage.getItem(PREVIEW_STORAGE_KEY);
    if (!raw) return null;
    if (raw === 'enforce') return { forcedKeys: new Set<string>(), forceAll: false };
    if (raw.startsWith('lock:')) {
      const keys = raw.slice('lock:'.length).split(',').map((k) => k.trim()).filter(Boolean);
      return { forcedKeys: new Set<string>(keys), forceAll: keys.includes('all') };
    }
  } catch {
    return null;
  }
  return null;
};

export interface ComputeEntitlementInput {
  key: EntitlementFeatureKey;
  entitlements: EntitlementsPayload | null;
  /** Old UI logic for legacy keys when there is no payload */
  legacyFallback: boolean;
  learnedSlug: string | null;
  preview: GatingPreview | null;
}

/** Pure decision (exported for tests). */
export const computeEntitlement = ({
  key,
  entitlements,
  legacyFallback,
  learnedSlug,
  preview,
}: ComputeEntitlementInput): EntitlementState => {
  const slug = learnedSlug ?? entitlements?.upsellPlanSlugs?.[key] ?? null;

  if (LEGACY_UI_KEYS.has(key)) {
    const allowed = entitlements ? entitlements.features[key] === true : legacyFallback;
    return { allowed, locked: !allowed, upsellPlanSlug: slug, mode: entitlements?.mode ?? 'off' };
  }

  const mode: GatingMode = preview ? 'enforce' : entitlements?.mode ?? 'off';
  if (mode !== 'enforce' || (!entitlements && !preview)) {
    return { allowed: true, locked: false, upsellPlanSlug: slug, mode };
  }
  const forced = preview ? preview.forceAll || preview.forcedKeys.has(key) : false;
  const allowed = forced ? false : entitlements ? entitlements.features[key] !== false : true;
  return { allowed, locked: !allowed, upsellPlanSlug: slug, mode };
};

export const useEntitlement = (key: EntitlementFeatureKey): EntitlementState => {
  const context = useContext(PackageContext);
  const learnedSlug = useGatingStore((s) => s.lockedSlugs[key] ?? null);
  const legacyName = LEGACY_HAS_FEATURE_NAME[key];

  return computeEntitlement({
    key,
    entitlements: context?.entitlements ?? null,
    legacyFallback: legacyName && context ? context.hasFeature(legacyName) : false,
    learnedSlug,
    preview: readGatingPreview(),
  });
};

/** Gating mode only (e.g. for hiding things in enforce). */
export const useGatingMode = (): GatingMode => {
  const context = useContext(PackageContext);
  return readGatingPreview() ? 'enforce' : context?.entitlements?.mode ?? 'off';
};
